var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { deleteEmbededDashboardConfig, exportEmbeddedDashboard, getEmbeddedDashboardConfigurations, saveEmbeddedDashboardConfiguration, mergeSettings } from "@core/services/services";
import { UXPContext } from "@core/context/Context";
import { DEFAULT_BREAKPOINTS, removeDummyWidgets } from "../dashboardUtils";
import { DashboardSkeleton } from "../DashboardSkeleton";
import { WidgetDrawerType } from "../widget-drawer/WidgetDrawer";
import { registerFloatingActionSection, unregisterFloatingActionSection, FloatingActionSectionType, FloatingActionButtonVisibility } from "@core/floating-actions/floatingActionsRegistry";
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
import useToast from "@notifications/Toast/useToast";
import useAlert from "@notifications/Alerts/useAlerts";
import Dashboard from "../Dashboard";
import DashboardConfigPanel from "../configurations/DashboardConfigPanel";
import DashboardSettingsModal from "../configurations/DashboardSettingsModal";
import BackgroundRenderer from "../BackgroundRenderer";
function normalizeDashboardIds(ids) {
    return ids.map(entry => typeof entry === 'string' ? { id: entry, label: entry } : entry);
}
const EmbeddedDashboardBase = ({ ids, defaultConfiguration, allowToConfigure = false, enableUserGroupLayouts = false, enableResponsiveLayouts = true, overlayMode = false, objectType, enableBackgroundConfig = false, autoPassedProps, globalDashboardSettings, }) => {
    var _a, _b, _c, _d, _e;
    const context = useContext(UXPContext);
    const alerts = useAlert();
    const toast = useToast();
    const { configuredashboard } = useSearchParams();
    const { navigate, generateURL } = useRouterContext();
    const normalizedIds = useMemo(() => normalizeDashboardIds(ids), [ids]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [dashobardDetails, setDashboardDetails] = useState(null);
    const [selectedUserGroup, setSelectedUserGroup] = useState(null);
    const [widgets, setWidgets] = useState([]);
    const [layouts, setLayouts] = useState({});
    const [selectedBreakpoint, setSelectedBreakpoint] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [backgroundConfig, setBackgroundConfig] = useState(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [gridSettings, setGridSettings] = useState(null);
    const [storedBehaviorSettings, setStoredBehaviorSettings] = useState(null);
    const [bgRefreshKey, setBgRefreshKey] = useState(0);
    const dashboardContainerRef = useRef(null);
    const floatingActionsControlsRef = useRef(null);
    const isEditingRef = useRef(isEditing);
    useEffect(() => { isEditingRef.current = isEditing; }, [isEditing]);
    const isLoadingRef = useRef(isLoading);
    useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
    useEffect(() => {
        var _a;
        setSelectedUserGroup(((_a = context === null || context === void 0 ? void 0 : context.userDetails) === null || _a === void 0 ? void 0 : _a.UserGroupID) || null);
    }, [context]);
    useEffect(() => {
        setIsEditing(configuredashboard == '1');
    }, [configuredashboard,]);
    useEffect(() => {
        var _a, _b, _c;
        function processDetails() {
            var _a, _b, _c;
            if (!dashobardDetails) {
                console.log('[EmbeddedDashboard] No dashboard details available');
                return { widgets: [], layouts: {} };
            }
            const widgetConfigs = dashobardDetails.WidgetConfigurations;
            console.log('[EmbeddedDashboard] Processing details:', {
                enableUserGroupLayouts,
                selectedUserGroup,
                hasUserGroupLayouts: !!(widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.userGroupLayouts),
                userGroupLayouts: widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.userGroupLayouts,
                defaultWidgets: (_a = widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.widgets) === null || _a === void 0 ? void 0 : _a.length,
                defaultLayouts: Object.keys((widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.layouts) || {})
            });
            // If user-group layouts enabled and a group is selected
            if (enableUserGroupLayouts && selectedUserGroup) {
                const groupLayout = (_b = widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.userGroupLayouts) === null || _b === void 0 ? void 0 : _b[selectedUserGroup];
                if (groupLayout) {
                    console.log(`[EmbeddedDashboard] Loading user group layout for: ${selectedUserGroup}`, {
                        widgets: (_c = groupLayout.widgets) === null || _c === void 0 ? void 0 : _c.length,
                        layouts: Object.keys(groupLayout.layouts || {})
                    });
                    return {
                        widgets: groupLayout.widgets || [],
                        layouts: groupLayout.layouts || {}
                    };
                }
                else {
                    console.log(`[EmbeddedDashboard] No custom layout found for user group: ${selectedUserGroup}, using default`);
                }
            }
            // Fall back to default layout
            console.log('[EmbeddedDashboard] Loading default layout');
            return {
                widgets: (widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.widgets) || [],
                layouts: (widgetConfigs === null || widgetConfigs === void 0 ? void 0 : widgetConfigs.layouts) || {}
            };
        }
        const { widgets: rawWidgets, layouts } = processDetails();
        // Adapt stored widget data: old saves use .widget (component) and .props (configuredProps)
        const widgets = rawWidgets.map((w) => {
            var _a, _b, _c, _d, _e;
            return (Object.assign(Object.assign({}, w), { component: (_b = (_a = w.component) !== null && _a !== void 0 ? _a : w.widget) !== null && _b !== void 0 ? _b : null, configuredProps: (_d = (_c = w.configuredProps) !== null && _c !== void 0 ? _c : w.props) !== null && _d !== void 0 ? _d : {}, hasConfigured: (_e = w.hasConfigured) !== null && _e !== void 0 ? _e : false }));
        });
        setWidgets(widgets);
        setLayouts(layouts);
        // Load background config from stored configuration (not user-group specific)
        setBackgroundConfig(((_a = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _a === void 0 ? void 0 : _a.backgroundConfig) || null);
        // Load grid settings from stored configuration (not user-group specific)
        setGridSettings(((_b = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _b === void 0 ? void 0 : _b.gridSettings) || null);
        // Load per-dashboard behavior settings stored under __settings__ key
        setStoredBehaviorSettings(((_c = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _c === void 0 ? void 0 : _c.__settings__) || null);
    }, [dashobardDetails, selectedUserGroup, enableUserGroupLayouts]);
    const findAndLoadDetails = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isEditingRef.current || isLoadingRef.current)
            return;
        setIsLoading(true);
        let details = null;
        for (const { id } of normalizedIds) {
            details = yield getEmbeddedDashboardConfigurations(context, id);
            if (!!details) {
                console.log(`[EmbeddedDashboard] Found database record for ID: ${id}`);
                setSelectedDashboardId(id);
                setDashboardDetails(details);
                break;
            }
        }
        // If no database records found, always set the fallback ID so saves work
        if (!details) {
            const genericId = normalizedIds[normalizedIds.length - 1].id; // Last ID is most generic
            setSelectedDashboardId(genericId);
            if (defaultConfiguration) {
                console.log(`[EmbeddedDashboard] No database records found. Using default configuration with ID: ${genericId}`);
                setDashboardDetails({
                    Key: genericId,
                    ID: genericId,
                    WidgetConfigurations: defaultConfiguration
                });
            }
            else {
                console.log(`[EmbeddedDashboard] No database records found. Starting empty with ID: ${genericId}`);
            }
        }
        setIsLoading(false);
    }), [context, ids, defaultConfiguration]);
    // initial render. find the first match and load.
    useEffect(() => {
        findAndLoadDetails();
    }, [findAndLoadDetails]);
    const handleDashboardSelect = useCallback((id) => __awaiter(void 0, void 0, void 0, function* () {
        console.log('[EmbeddedDashboard] Dashboard selected:', id);
        setIsLoading(true);
        setSelectedDashboardId(id);
        const details = yield getEmbeddedDashboardConfigurations(context, id);
        console.log('[EmbeddedDashboard] Dashboard details loaded:', details);
        setDashboardDetails(details);
        setIsLoading(false);
    }), [context]);
    const handleUserGroupSelect = useCallback((userGroup) => __awaiter(void 0, void 0, void 0, function* () {
        console.log('[EmbeddedDashboard] User group selected:', userGroup === null || userGroup === void 0 ? void 0 : userGroup.UserGroupID);
        setSelectedUserGroup(userGroup === null || userGroup === void 0 ? void 0 : userGroup.UserGroupID);
    }), []);
    const handleBreakpointSelect = useCallback((breakpoint) => {
        setSelectedBreakpoint(breakpoint);
    }, []);
    const handleSave = useCallback((updatedWidgets, updatedLayouts, backgroundConfigOverride) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedDashboardId && normalizedIds.length > 1 && isEditing) {
            alerts.error('Please select a dashboard to save the configurations');
            return false;
        }
        setWidgets(updatedWidgets);
        setLayouts(updatedLayouts);
        const userGroupToSave = enableUserGroupLayouts ? selectedUserGroup : undefined;
        // Effective background: explicit override takes priority, then current state
        const bgConfig = backgroundConfigOverride !== undefined
            ? backgroundConfigOverride
            : backgroundConfig;
        // Build config: spread stored values, then explicitly set or delete backgroundConfig
        // so that clearing (bgConfig = null) actually removes the key rather than leaving
        // the old stored value in place
        let configToSave = Object.assign({}, dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations);
        if (bgConfig) {
            configToSave.backgroundConfig = bgConfig;
        }
        else {
            delete configToSave.backgroundConfig;
        }
        if (!!userGroupToSave) {
            if (!configToSave.userGroupLayouts) {
                configToSave.userGroupLayouts = {};
            }
            configToSave.userGroupLayouts[userGroupToSave] = {
                widgets: updatedWidgets,
                layouts: updatedLayouts
            };
        }
        else {
            configToSave.widgets = updatedWidgets;
            configToSave.layouts = updatedLayouts;
        }
        const success = yield saveEmbeddedDashboardConfiguration(context, selectedDashboardId || normalizedIds[normalizedIds.length - 1].id, configToSave);
        // Keep dashboardDetails in sync so subsequent spreads use the freshly saved config
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: configToSave }) : prev);
        }
        return success;
    }), [selectedDashboardId, ids, dashobardDetails, enableUserGroupLayouts, selectedUserGroup, context, alerts, isEditing, backgroundConfig]);
    // Called when user copies config from another dashboard ID (optionally a specific usergroup layout)
    const handleCopyFrom = useCallback((sourceId, sourceUserGroup) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const sourceDetails = yield getEmbeddedDashboardConfigurations(context, sourceId);
        if (!sourceDetails) {
            alerts.error(context.$L('uxp-core.text.unable-to-load-source'));
            return;
        }
        // Pick which sub-config to copy from source
        let configToCopy;
        if (sourceUserGroup && ((_b = (_a = sourceDetails.WidgetConfigurations) === null || _a === void 0 ? void 0 : _a.userGroupLayouts) === null || _b === void 0 ? void 0 : _b[sourceUserGroup])) {
            configToCopy = sourceDetails.WidgetConfigurations.userGroupLayouts[sourceUserGroup];
        }
        else {
            configToCopy = {
                widgets: ((_c = sourceDetails.WidgetConfigurations) === null || _c === void 0 ? void 0 : _c.widgets) || [],
                layouts: ((_d = sourceDetails.WidgetConfigurations) === null || _d === void 0 ? void 0 : _d.layouts) || {}
            };
        }
        // Apply to the current target slot (respecting active usergroup)
        const targetId = selectedDashboardId || normalizedIds[normalizedIds.length - 1].id;
        let newConfig = Object.assign({}, ((dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) || { widgets: [], layouts: {} }));
        if (selectedUserGroup && enableUserGroupLayouts) {
            newConfig = Object.assign(Object.assign({}, newConfig), { userGroupLayouts: Object.assign(Object.assign({}, newConfig.userGroupLayouts), { [selectedUserGroup]: configToCopy }) });
        }
        else {
            newConfig = Object.assign(Object.assign({}, newConfig), { widgets: configToCopy.widgets, layouts: configToCopy.layouts });
        }
        const success = yield saveEmbeddedDashboardConfiguration(context, targetId, newConfig);
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: newConfig }) : prev);
            toast.success(context.$L('uxp-core.text.configuration-copied'));
        }
        else {
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [context, selectedDashboardId, normalizedIds, dashobardDetails, selectedUserGroup, enableUserGroupLayouts, alerts, toast]);
    // Called from settings modal to clear all widgets on the current dashboard
    const handleClearWidgets = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const targetId = selectedDashboardId || normalizedIds[normalizedIds.length - 1].id;
        let cleared;
        if (selectedUserGroup && enableUserGroupLayouts) {
            cleared = Object.assign(Object.assign({}, dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations), { userGroupLayouts: Object.assign(Object.assign({}, (_a = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _a === void 0 ? void 0 : _a.userGroupLayouts), { [selectedUserGroup]: { widgets: [], layouts: {} } }) });
        }
        else {
            cleared = Object.assign(Object.assign({}, dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations), { widgets: [], layouts: {} });
        }
        const success = yield saveEmbeddedDashboardConfiguration(context, targetId, cleared);
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: cleared }) : prev);
            setWidgets([]);
            setLayouts({});
            toast.success(context.$L('uxp-core.text.widgets-cleared'));
        }
        else {
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [context, selectedDashboardId, normalizedIds, dashobardDetails, selectedUserGroup, enableUserGroupLayouts, alerts, toast]);
    // Called from settings modal to save background config (explicit, not auto-save)
    const handleSaveBackground = useCallback((config) => __awaiter(void 0, void 0, void 0, function* () {
        setBackgroundConfig(config);
        const cleaned = removeDummyWidgets(widgets, layouts);
        const success = yield handleSave(cleaned.widgets, cleaned.layouts, config);
        if (success)
            toast.success(context.$L('uxp-core.text.changes-saved'));
        else
            toast.error(context.$L('uxp-core.text.unable-to-save'));
    }), [widgets, layouts, handleSave, context]);
    // Called from danger zone in settings modal
    const handleClearBackground = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield handleSaveBackground(null);
    }), [handleSaveBackground]);
    // Called from settings modal to push current config to multiple dashboard × usergroup combinations
    const handleApplyTo = useCallback((targets) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        // Determine the current config slice being edited
        let currentConfig;
        if (selectedUserGroup && enableUserGroupLayouts && ((_b = (_a = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _a === void 0 ? void 0 : _a.userGroupLayouts) === null || _b === void 0 ? void 0 : _b[selectedUserGroup])) {
            currentConfig = dashobardDetails.WidgetConfigurations.userGroupLayouts[selectedUserGroup];
        }
        else {
            currentConfig = {
                widgets: ((_c = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _c === void 0 ? void 0 : _c.widgets) || [],
                layouts: ((_d = dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) === null || _d === void 0 ? void 0 : _d.layouts) || {}
            };
        }
        let anyFailed = false;
        for (const targetId of targets.dashboardIds) {
            const targetDetails = yield getEmbeddedDashboardConfigurations(context, targetId);
            let newConfig = Object.assign({}, ((targetDetails === null || targetDetails === void 0 ? void 0 : targetDetails.WidgetConfigurations) || { widgets: [], layouts: {} }));
            if (targets.userGroups.length > 0 && enableUserGroupLayouts) {
                const updatedGroups = Object.assign({}, newConfig.userGroupLayouts);
                for (const ug of targets.userGroups) {
                    updatedGroups[ug] = currentConfig;
                }
                newConfig = Object.assign(Object.assign({}, newConfig), { userGroupLayouts: updatedGroups });
            }
            else {
                newConfig = Object.assign(Object.assign({}, newConfig), { widgets: currentConfig.widgets, layouts: currentConfig.layouts });
            }
            const ok = yield saveEmbeddedDashboardConfiguration(context, targetId, newConfig);
            if (!ok)
                anyFailed = true;
        }
        if (anyFailed)
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        else
            toast.success(context.$L('uxp-core.text.configuration-applied'));
    }), [context, selectedUserGroup, enableUserGroupLayouts, dashobardDetails, alerts, toast]);
    const handleSaveGridSettings = useCallback((settings) => __awaiter(void 0, void 0, void 0, function* () {
        const targetId = selectedDashboardId || normalizedIds[normalizedIds.length - 1].id;
        const newConfig = Object.assign(Object.assign({}, ((dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) || { widgets: [], layouts: {} })), { gridSettings: settings });
        const success = yield saveEmbeddedDashboardConfiguration(context, targetId, newConfig);
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: newConfig }) : prev);
            setGridSettings(settings);
            toast.success(context.$L('uxp-core.text.settings-saved'));
        }
        else {
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [context, selectedDashboardId, normalizedIds, dashobardDetails, alerts, toast]);
    const handleSaveDashboardSpecificSettings = useCallback((settings) => __awaiter(void 0, void 0, void 0, function* () {
        const targetId = selectedDashboardId || normalizedIds[normalizedIds.length - 1].id;
        const newConfig = Object.assign(Object.assign({}, ((dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) || { widgets: [], layouts: {} })), { __settings__: settings });
        const success = yield saveEmbeddedDashboardConfiguration(context, targetId, newConfig);
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: newConfig }) : prev);
            setStoredBehaviorSettings(settings);
            toast.success(context.$L('uxp-core.text.settings-saved'));
        }
        else {
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [context, selectedDashboardId, normalizedIds, dashobardDetails, alerts, toast]);
    // Combined save: builds one config update so grid settings and behavior settings
    // don't overwrite each other via stale dashobardDetails closures
    const handleSaveCombinedSettings = useCallback((newGridSettings, newBehaviorSettings) => __awaiter(void 0, void 0, void 0, function* () {
        const targetId = selectedDashboardId || normalizedIds[normalizedIds.length - 1].id;
        const newConfig = Object.assign(Object.assign({}, ((dashobardDetails === null || dashobardDetails === void 0 ? void 0 : dashobardDetails.WidgetConfigurations) || { widgets: [], layouts: {} })), { gridSettings: newGridSettings, __settings__: newBehaviorSettings });
        const success = yield saveEmbeddedDashboardConfiguration(context, targetId, newConfig);
        if (success) {
            setDashboardDetails(prev => prev ? Object.assign(Object.assign({}, prev), { WidgetConfigurations: newConfig }) : prev);
            setGridSettings(newGridSettings);
            setStoredBehaviorSettings(newBehaviorSettings);
            toast.success(context.$L('uxp-core.text.settings-saved'));
        }
        else {
            alerts.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [context, selectedDashboardId, normalizedIds, dashobardDetails, alerts, toast]);
    const handleBgRefresh = useCallback(() => {
        setBgRefreshKey(k => k + 1);
    }, []);
    const handleWidgetDrawerChanges = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        if (event.widgets && event.layouts) {
            return handleSave(event.widgets, event.layouts);
        }
        return true;
    }), [handleSave]);
    const handleContainerMount = useCallback((ref) => {
        dashboardContainerRef.current = ref;
    }, []);
    const handleRestore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const confirmed = yield alerts.confirm({
            title: context.$L('uxp-core.text.are-you-sure'),
            content: context.$L('uxp-core.text.restore-to-defaults-warning')
        });
        if (confirmed) {
            const res = yield deleteEmbededDashboardConfig(context, selectedDashboardId);
            if (res)
                toast.success(context.$L('uxp-core.text.restored'));
            else
                alerts.error(context.$L('uxp-core.text.restore-failed'));
            findAndLoadDetails();
        }
    }), [selectedDashboardId, context]);
    const handleEnterEdit = useCallback(() => {
        navigate(generateURL({ searchParams: { 'configuredashboard': '1' } }));
    }, [context]);
    const handleExitEdit = useCallback(() => {
        // Simply exit edit mode - changes are already saved by Dashboard component
        toast.success(context.$L('uxp-core.text.changes-saved'));
        navigate(generateURL({ removeParams: ['configuredashboard'] }));
    }, [context]);
    // Merge autoPassedProps (global filters passed from parent) into each widget's props
    const mergedWidgets = useMemo(() => {
        if (!autoPassedProps || Object.keys(autoPassedProps).length === 0)
            return widgets;
        return widgets.map(w => (Object.assign(Object.assign({}, w), { configuredProps: Object.assign(Object.assign({}, (w.configuredProps || {})), autoPassedProps) })));
    }, [widgets, autoPassedProps]);
    const effectiveBreakpoints = useMemo(() => {
        const effectiveSettings = mergeSettings(globalDashboardSettings || null, storedBehaviorSettings, gridSettings);
        const activeBreakpointMap = new Map(effectiveSettings.activeBreakpoints.map(b => [b.key, b]));
        const base = (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.columns)
            ? Object.fromEntries(Object.entries(DEFAULT_BREAKPOINTS).map(([k, v]) => [k, Object.assign(Object.assign({}, v), { columns: gridSettings.columns })]))
            : DEFAULT_BREAKPOINTS;
        return Object.fromEntries(Object.entries(base)
            .filter(([k]) => activeBreakpointMap.has(k))
            .map(([k, v]) => {
            const activeEntry = activeBreakpointMap.get(k);
            return [k, Object.assign(Object.assign({}, v), { width: activeEntry.width, columns: activeEntry.columns })];
        }));
    }, [gridSettings, globalDashboardSettings, storedBehaviorSettings]);
    const customActions = useMemo(() => [
        {
            icon: 'phl cloud-arrow-down',
            onClick: () => exportEmbeddedDashboard(context, selectedDashboardId, true),
            tooltip: context.$L('uxp-core.text.export'),
            variant: 'secondary',
            visibility: FloatingActionButtonVisibility.Edit
        },
        ...(defaultConfiguration ? [{
                icon: 'phl arrow-counter-clockwise',
                onClick: () => handleRestore(),
                tooltip: context.$L('uxp-core.text.restore-to-defaults'),
                variant: 'secondary',
                visibility: FloatingActionButtonVisibility.Edit
            }] : []),
        {
            icon: 'phl gear',
            onClick: () => setShowSettingsModal(true),
            tooltip: context.$L('uxp-core.text.dashboard-settings'),
            variant: 'secondary',
            visibility: FloatingActionButtonVisibility.Edit
        }
    ], [context, selectedDashboardId, handleRestore, defaultConfiguration]);
    // Effective behavior: stored settings take priority over prop defaults
    const effectiveOverlayMode = (_a = storedBehaviorSettings === null || storedBehaviorSettings === void 0 ? void 0 : storedBehaviorSettings.overlayMode) !== null && _a !== void 0 ? _a : overlayMode;
    const effectiveEnableBackgroundConfig = (_b = storedBehaviorSettings === null || storedBehaviorSettings === void 0 ? void 0 : storedBehaviorSettings.enableBackgroundConfig) !== null && _b !== void 0 ? _b : enableBackgroundConfig;
    const effectiveEnableUserGroupLayouts = (_c = storedBehaviorSettings === null || storedBehaviorSettings === void 0 ? void 0 : storedBehaviorSettings.enableUserGroupLayouts) !== null && _c !== void 0 ? _c : enableUserGroupLayouts;
    const effectiveEnableResponsiveLayouts = (_d = storedBehaviorSettings === null || storedBehaviorSettings === void 0 ? void 0 : storedBehaviorSettings.enableResponsiveLayouts) !== null && _d !== void 0 ? _d : enableResponsiveLayouts;
    // In overlay mode default to true; in standard mode default to false (explicit save required)
    const effectiveFreePositioning = effectiveOverlayMode
        ? ((gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.allowFreePositioning) !== false)
        : ((gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.allowFreePositioning) === true);
    // Register dashboard floating action section
    useEffect(() => {
        if (allowToConfigure) {
            registerFloatingActionSection({
                id: 'dashboard',
                type: FloatingActionSectionType.Dashboard,
                widgets: widgets,
                layouts: layouts,
                enableEdit: allowToConfigure,
                isEditing: isEditing,
                onEnterEditMode: handleEnterEdit,
                onExitEditMode: handleExitEdit,
                enableAdd: true,
                addButtonVisibility: FloatingActionButtonVisibility.Edit,
                onChangeWidgetDrawer: handleWidgetDrawerChanges,
                customActions: customActions,
                isBounded: effectiveOverlayMode,
                maxColumns: 40,
                widgetDrawerConfig: {
                    defaultType: WidgetDrawerType.Widgets,
                    showTypeFilter: false
                },
                onRegister: (controls) => {
                    // Capture control methods for programmatic access
                    floatingActionsControlsRef.current = controls;
                }
            });
        }
        return () => {
            if (allowToConfigure) {
                unregisterFloatingActionSection('dashboard');
                floatingActionsControlsRef.current = null;
            }
        };
    }, [allowToConfigure, isEditing, widgets, layouts, customActions, handleWidgetDrawerChanges, handleEnterEdit, handleExitEdit, effectiveOverlayMode]);
    const dashboard = (_jsx(Dashboard, { widgets: mergedWidgets, layouts: layouts, isEditing: isEditing, onSave: handleSave, breakpoints: effectiveBreakpoints, margin: (effectiveOverlayMode && effectiveFreePositioning) ? [0, 0] : ((gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.margin) != null ? [gridSettings.margin, gridSettings.margin] : undefined), padding: (effectiveOverlayMode && effectiveFreePositioning) ? [0, 0] : ((gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.containerPadding) != null ? [gridSettings.containerPadding, gridSettings.containerPadding] : undefined), allowFreePositioning: effectiveFreePositioning, onContainerMount: handleContainerMount, breakpointOverride: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null, showSpacerWidget: !effectiveOverlayMode, overlayMode: effectiveOverlayMode, emptyPlaceholder: {
            show: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.showEmptyPlaceholder) !== false,
            onClick: () => {
                var _a;
                (_a = floatingActionsControlsRef.current) === null || _a === void 0 ? void 0 : _a.openWidgetDrawer();
            }
        }, showGridlines: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.showGridlines) === true || ((_e = globalDashboardSettings === null || globalDashboardSettings === void 0 ? void 0 : globalDashboardSettings.defaults) === null || _e === void 0 ? void 0 : _e.showGridlines) === true }));
    // Use the wrapper whenever overlay mode is active OR background config is enabled
    // (background needs position:relative container to anchor its absolute positioning)
    const needsWrapper = effectiveOverlayMode || effectiveEnableBackgroundConfig;
    return _jsxs(_Fragment, { children: [needsWrapper
                ? (_jsxs("div", { className: "uxpcore_embeddeddashboard__overlay-wrapper", children: [_jsx(BackgroundRenderer, { config: backgroundConfig, passedProps: autoPassedProps }, bgRefreshKey), isLoading ? _jsx(DashboardSkeleton, {}) : dashboard] }))
                : (isLoading ? _jsx(DashboardSkeleton, {}) : dashboard), isEditing && (_jsx(DashboardConfigPanel, { containerRef: dashboardContainerRef, breakpointsSelection: {
                    enable: effectiveEnableResponsiveLayouts && !effectiveOverlayMode,
                    selected: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null,
                    onSelect: handleBreakpointSelect,
                    breakpoints: DEFAULT_BREAKPOINTS
                }, userGroupsSelection: {
                    enable: effectiveEnableUserGroupLayouts,
                    selected: selectedUserGroup,
                    onSelect: handleUserGroupSelect
                }, dashboardSelection: {
                    enable: true,
                    ids: normalizedIds,
                    selected: selectedDashboardId,
                    onSelect: handleDashboardSelect
                } })), _jsx(DashboardSettingsModal, { show: showSettingsModal, onClose: () => setShowSettingsModal(false), dashboardIds: normalizedIds, currentDashboardId: selectedDashboardId, enableUserGroupLayouts: effectiveEnableUserGroupLayouts, onCopyFrom: normalizedIds.length > 1 ? handleCopyFrom : undefined, onApplyTo: normalizedIds.length > 1 ? handleApplyTo : undefined, onClearWidgets: handleClearWidgets, onClearBackground: effectiveEnableBackgroundConfig ? handleClearBackground : undefined, gridSettings: gridSettings, onSaveGridSettings: allowToConfigure ? handleSaveGridSettings : undefined, overlayMode: effectiveOverlayMode, backgroundConfig: effectiveEnableBackgroundConfig ? backgroundConfig : undefined, onSaveBackground: effectiveEnableBackgroundConfig && allowToConfigure ? handleSaveBackground : undefined, objectType: objectType, onBgRefresh: effectiveEnableBackgroundConfig ? handleBgRefresh : undefined, globalDashboardSettings: globalDashboardSettings, dashboardSpecificSettings: storedBehaviorSettings, onSaveDashboardSpecificSettings: allowToConfigure ? handleSaveDashboardSpecificSettings : undefined, onSaveCombinedSettings: allowToConfigure ? handleSaveCombinedSettings : undefined })] });
};
/**
 * @export
 * Embedded dashboard component for rendering dashboards within pages (without navigation)
 *
 * @example
 * // Basic usage - Simple embedded dashboard
 * <EmbeddedDashboard
 *   ids={["myapp/dashboard/main"]}
 * />
 *
 * @example
 * // With default configuration from JSON file
 * import defaultConfig from './dashboards/equipment-default.json';
 *
 * <EmbeddedDashboard
 *   ids={["equipment/dashboard/chiller-123", "equipment/dashboard/chiller", "equipment/dashboard"]}
 *   defaultConfiguration={defaultConfig}
 *   allowToConfigure={true}
 * />
 *
 * @example
 * // Advanced usage - With user group layouts and responsive breakpoints
 * <EmbeddedDashboard
 *   ids={["equipment/dashboard/chiller-123", "equipment/dashboard/chiller", "equipment/dashboard"]}
 *   allowToConfigure={true}
 *   enableUserGroupLayouts={true}
 *   enableResponsiveLayouts={true}
 * />
 */
export const EmbeddedDashboard = memo(EmbeddedDashboardBase);
